import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@dopomogai/ui';
import { Clock, Lock, FileText, Loader2 } from 'lucide-react';
import { leadsService } from '@dopomogai/api-client';

import { useAssessmentState } from './hooks/useAssessmentState';
import { getQuestionsForTrack } from './data/questions';
import { getAssessmentResult } from './data/index';
import { Department } from './engine/types';

import { TrackSelector } from './ui/TrackSelector';
import { QuizInterface } from './ui/QuizInterface';
import { BlueprintResult } from './ui/BlueprintResult';
import { EmailGate } from './ui/EmailGate'; 

// Helper
const getSmartlookUrl = (): string | undefined => {
  try {
    // @ts-ignore
    if (typeof window !== 'undefined' && window.smartlook && window.smartlook.playUrl) {
       // @ts-ignore
       return window.smartlook.playUrl;
    }
  } catch (e) { console.warn("Smartlook not found", e); }
  return undefined;
};

export const AssessmentSection: React.FC = () => {
  const containerRef = useRef<HTMLElement>(null);
  
  // 1. ENGINE HOOK (includes submitEmail)
  const { state, startTrack, answerQuestion, undoLastAnswer, resetAssessment, getCurrentQuestion, submitEmail } = useAssessmentState();

  // STATES: 'idle' | 'active' | 'analyzing' | 'gated' | 'result'
  const [viewStep, setViewStep] = useState<'idle'|'active'|'analyzing'|'gated'|'result'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 2. STATE SYNCHRONIZATION (Fixes Reload Issue)
  useEffect(() => {
    // If idle, show selector
    if (state.status === 'idle') {
        setViewStep('idle');
        return;
    }
    
    // If active, show quiz
    if (state.status === 'active') {
        setViewStep('active');
        return;
    }

    // If complete...
    if (state.status === 'complete') {
        // ...and we have the email (unlocked), show result immediately
        if (state.userEmail) {
            setViewStep('result');
        } 
        // ...otherwise, if we are not already gating/analyzing, trigger analysis
        else if (viewStep !== 'gated' && viewStep !== 'analyzing' && viewStep !== 'result') {
            setViewStep('analyzing');
            const timer = setTimeout(() => {
                setViewStep('gated');
            }, 1500);
            return () => clearTimeout(timer);
        }
    }
  }, [state.status, state.userEmail]); 

  // SCROLL HELPER
  const scrollToTop = () => {
    if (containerRef.current) {
        const y = containerRef.current.getBoundingClientRect().top + window.pageYOffset - 100;
        window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handleStartTrack = (id: any) => {
    startTrack(id);
    setTimeout(() => { scrollToTop(); }, 100);
  };

  const handleRetake = () => {
      resetAssessment();
      setTimeout(() => { scrollToTop(); }, 100); 
  };

  const handleAnswer = (qId: string, oId: string) => {
    answerQuestion(qId, oId);
  };

  const handleUnlock = async (email: string) => {
    if (!state.calculation) return;
    
    setIsSubmitting(true);

    try {
        const fullResult = getAssessmentResult(state.calculation.primaryResultId);
        if (!fullResult) throw new Error("Result missing");

        const dept = (state.calculation.context.department || 'default') as Department | 'default';
        const dynamicBlock = fullResult.dynamic_content[dept] || fullResult.dynamic_content['default'];

        const payload = {
            id: fullResult.id,
            track: fullResult.track,
            title: fullResult.title,
            verdict: fullResult.verdict,
            diagnosis: fullResult.diagnosis,
            solution: fullResult.solution,
            roadmap: {
                title: dynamicBlock.roadmap_title,
                steps: dynamicBlock.steps
            },
            intelligence: fullResult.intelligence,
            cta: fullResult.cta
        };

        const visitorId = typeof window !== 'undefined' 
            ? localStorage.getItem('dopomogai_visitor_id') || undefined 
            : undefined;

        await leadsService.saveRoadmap({
            email: email,
            result: payload,
            visitor_id: visitorId,
            smartlook_url: getSmartlookUrl()
        });
        
        // PERSIST UNLOCK
        submitEmail(email); // This saves email to localStorage via hook
        
        setViewStep('result');
        setTimeout(() => { scrollToTop(); }, 100);

    } catch (e) {
        console.error("Save failed:", e);
        // On error, we still unlock to not block user, but don't persist email maybe?
        // Actually, safer to persist so they don't get stuck.
        submitEmail(email); 
        setViewStep('result');
    } finally {
        setIsSubmitting(false);
    }
  };

  const handleBooking = (calParams: string = '{}') => {
     let parsedParams: any = {};
     try { parsedParams = JSON.parse(calParams); } catch (e) {}
     
     const bookingEl = document.getElementById('booking');
     if (bookingEl) {
        bookingEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
     } else {
        const baseUrl = "https://cal.com/dopomogai/strategy";
        const note = parsedParams.note || "Assessment Result";
        const params = new URLSearchParams({ notes: note });
        window.open(`${baseUrl}?${params.toString()}`, '_blank');
     }
  };

  // Progress Bar
  const activeQuestion = getCurrentQuestion();
  let progress = 0;
  if (state.trackId) {
     const allQuestions = getQuestionsForTrack(state.trackId, state.answers);
     const answeredCount = Object.keys(state.answers).length;
     progress = (answeredCount / allQuestions.length) * 100;
  }

  return (
    <section ref={containerRef} id="assessment" className="py-24 bg-slate-900 border-b-2 border-slate-800 scroll-mt-20 min-h-screen flex flex-col justify-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-20 pointer-events-none bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:30px_30px]" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 w-full relative z-10">
        
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <Badge 
            text="OPERATIONAL ARCHITECTURE" 
            accentColor={viewStep === 'result' ? 'emerald' : 'slate'} 
            className="mb-4 bg-slate-800 text-white border-slate-700" 
          />
          
          <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight mb-4 transition-all">
             {viewStep === 'idle' && "Initialize Your Transformation."}
             {(viewStep === 'active' || viewStep === 'analyzing') && "Diagnosing Infrastructure..."}
             {viewStep === 'gated' && "Blueprint Compiled."}
             {viewStep === 'result' && "Analysis Complete."}
          </h2>
          
          <p className="text-lg text-slate-400 font-medium">
             {viewStep === 'idle' && "Select your operating profile to run a 1-minute diagnostic and unlock your custom Architecture Blueprint."}
             {viewStep === 'active' && "Select the option that best describes your current operational reality."}
             {viewStep === 'gated' && "Unlock your sovereign architecture plan below."}
             {viewStep === 'result' && "Your custom deployment plan is ready."}
          </p>
        </div>

        <div className="min-h-[600px] relative">
            <AnimatePresence mode="wait">
                
                {viewStep === 'idle' && (
                    <motion.div key="selector" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <TrackSelector onSelect={handleStartTrack} />
                    </motion.div>
                )}

                {viewStep === 'active' && activeQuestion && state.trackId && (
                    <motion.div key="quiz" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <QuizInterface 
                            trackId={state.trackId}
                            activeQuestion={activeQuestion}
                            progress={progress}
                            onAnswer={(q, o) => answerQuestion(q, o)}
                            onBack={undoLastAnswer}
                        />
                    </motion.div>
                )}

                {viewStep === 'analyzing' && (
                   <motion.div 
                     key="spinner"
                     initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                     className="absolute inset-0 flex flex-col items-center justify-center z-50"
                   >
                      <Loader2 size={48} className="animate-spin text-emerald-500 mb-4" />
                      <p className="font-bold text-white text-lg animate-pulse font-mono">COMPILING BLUEPRINT...</p>
                   </motion.div>
                )}

                {viewStep === 'gated' && (
                    <motion.div key="gate" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                        <EmailGate onSubmit={handleUnlock} isLoading={isSubmitting} />
                    </motion.div>
                )}

                {viewStep === 'result' && state.calculation && (
                    <motion.div key="result" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <BlueprintResult 
                            calculation={state.calculation} 
                            userEmail={state.userEmail || ''}
                            onRetake={handleRetake}
                            onBook={handleBooking}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>

        {(viewStep === 'idle' || viewStep === 'active') && (
           <div className="mt-12 border-t border-slate-800 pt-6 flex flex-wrap justify-center gap-6 md:gap-12 text-slate-500 font-bold text-xs uppercase tracking-widest font-mono">
              <div className="flex items-center gap-2"><Clock size={16} className="text-emerald-500"/> Est. Time: 1 Minute</div>
              <div className="flex items-center gap-2"><Lock size={16} className="text-emerald-500"/> No Login Required</div>
              <div className="flex items-center gap-2"><FileText size={16} className="text-emerald-500"/> Output: Strategic Blueprint</div>
           </div>
        )}

      </div>
    </section>
  );
};